import {
	type AppID,
	type NubeSDKRuntime,
	type NubeSDKRuntimeEvent,
	type NubeSDKRuntimeState,
	type NubeSDKState,
	WORKER_PRIVATE_STATE_PATHS,
	type WorkerInternalMessage,
	type WorkerMessage,
	isInternalEvent,
} from "@tiendanube/nube-sdk-internal-types";
import { removeObjectKeys } from "../../object-keys-utils";
import { styledComponentMessageHandler } from "../../styled-components/styledComponentMessageHandler";
import {
	createWorkerInternalMessageHandler,
	workerMessageHandler,
} from "../handler/worker-handler";

export function isInternalMessage(
	message: WorkerMessage | WorkerInternalMessage,
): message is WorkerInternalMessage {
	return isInternalEvent(message.type);
}

/**
 * Sets up communication between a web worker and the main application runtime for a specific app.
 *
 * Adds a message event listener to the worker to handle incoming messages, routing them to the appropriate handler based on message type and app ID. Also subscribes to all runtime events and forwards relevant state updates to the worker, excluding private state paths.
 *
 * @param id - The app ID associated with the worker.
 * @param worker - The web worker instance to communicate with.
 * @param runtime - The runtime managing application state and events.
 */
export function addWorkerEventListener(
	id: AppID,
	worker: Worker,
	runtime: NubeSDKRuntime,
) {
	const internalMessageHandler = createWorkerInternalMessageHandler(
		id,
		worker,
		{
			localStorage: localStorage,
			sessionStorage: sessionStorage,
		},
	);

	worker.addEventListener("message", (m: MessageEvent<string>) => {
		try {
			const message = JSON.parse(m.data) as
				| WorkerMessage
				| WorkerInternalMessage;

			try {
				if (String(message.app) === String(id)) {
					if (isInternalMessage(message)) {
						internalMessageHandler(message, runtime);
					} else {
						if (message.type === "ui:slot:set") {
							// process styled-components after the main handler
							styledComponentMessageHandler(message);
						}
						workerMessageHandler(message, runtime);
					}
					return;
				}
			} catch (err) {
				console.error(`Error handling worker message: ${err}`);
				return;
			}
			console.error(`App ${id} sent message with invalid id ${message.app}`);
		} catch (err) {
			console.error("Unable to parse worker app message");
		}
	});

	runtime.on(
		"*",
		(
			state: NubeSDKRuntimeState,
			event: NubeSDKRuntimeEvent,
			senderId,
			targetId,
		) => {
			if (!targetId || targetId === id) {
				const { apps, ...runtimeState } = state;

				const stateToSend: NubeSDKState = removeObjectKeys(
					runtimeState,
					WORKER_PRIVATE_STATE_PATHS,
				);

				worker.postMessage(
					JSON.stringify({
						type: event,
						state: stateToSend,
					}),
				);
			}
		},
	);
}
